export interface ShellUrlConfig {
    readonly userId: string;
    readonly appId: string;
    readonly sessionId: string;
    readonly streamGroupId: string;
    readonly applicationId: string;
    readonly location: string;
    readonly showStats: boolean;
    readonly streamStartupTimeoutMs: number | null;
    readonly controllerId: string;
    readonly hasControllerId: boolean;
    readonly hasRequiredStreamParams: boolean;
    readonly marketId: string;
    readonly platform: string;
    readonly glQrURL: string;
}

const DEFAULT_LOCATION = 'us-east-2';
const MIN_STREAM_STARTUP_TIMEOUT_MS = 1000;
const MAX_STREAM_STARTUP_TIMEOUT_MS = 300000;

const ALLOWED_REGION_PATTERN = /^[a-z]{2}-[a-z]+-\d$/;
const ALLOWED_TEXT_PATTERN = /^[a-zA-Z0-9_-]{1,64}$/;
const STREAM_GROUP_ID_PATTERN = /^sg-[a-zA-Z0-9]{5,}$/;
const APPLICATION_ID_PATTERN = /^a-[a-zA-Z0-9]{4,}$/;
const ALLOWED_GL_QR_URL_ORIGINS = new Set([
    'https://play.dev.gameloop.tv',
    'https://play.stage.gameloop.tv',
    'https://play.gameloop.tv'
]);

export class ShellUrlConfigService {
    public static readonly DEFAULT_STREAM_STARTUP_TIMEOUT_MS = 10000;

    /**
     * Maximum length in characters for a validated QR URL.
     *
     * Per {@link https://www.rfc-editor.org/rfc/rfc9110#section-4.1 | RFC 9110 §4.1},
     * all HTTP senders and recipients SHOULD support URIs with lengths of at
     * least 8000 octets. This floor is used as the validation threshold for
     * incoming `glQrURL` query parameters to reject excessively long payloads
     * while remaining compatible with modern CDNs (CloudFront 8 KB, Fastly 8 KB)
     * and browsers.
     *
     * @constant
     * @type {number}
     * @default
     */
    private static readonly MAX_QRURL_LENGTH = 8000;

    public static parseStreamStartupTimeoutMs(input: string | null | undefined): number | null {
        if (!input) {
            return null;
        }

        const value = Number(input.trim());
        if (!Number.isInteger(value) || value <= 0) {
            return null;
        }

        return Math.max(MIN_STREAM_STARTUP_TIMEOUT_MS, Math.min(value, MAX_STREAM_STARTUP_TIMEOUT_MS));
    }

    public parseFromWindow(locationSearch: string): ShellUrlConfig {
        const parameters = new URLSearchParams(locationSearch);

        const streamGroupId = this.validateId(parameters.get('streamGroupId') ?? '', STREAM_GROUP_ID_PATTERN);
        const applicationId = this.validateId(parameters.get('applicationId') ?? '', APPLICATION_ID_PATTERN);
        const location = this.sanitizeRegion(parameters.get('location'));
        const showStats = this.parseBoolean(parameters.get('showStats'), false);
        const streamStartupTimeoutMs = ShellUrlConfigService.parseStreamStartupTimeoutMs(parameters.get('streamStartupTimeoutMs'));
        const userId = this.sanitizeText(parameters.get('playerId'));
        const controllerId = this.sanitizeText(parameters.get('controllerId'));
        const appId = this.sanitizeText(parameters.get('app_id'));
        const sessionId = this.sanitizeText(parameters.get('sessionId'));
        const glQrURL = this.sanitizeGlQrUrl(parameters.get('glQrURL'));

        return {
            userId,
            appId,
            sessionId,
            streamGroupId,
            applicationId,
            location,
            showStats,
            streamStartupTimeoutMs,
            controllerId,
            hasControllerId: controllerId.length > 0,
            hasRequiredStreamParams: streamGroupId.length > 0 && applicationId.length > 0,
            marketId: this.sanitizeText(parameters.get('marketId')),
            platform: this.sanitizeText(parameters.get('platform')),
            glQrURL
        };
    }

    private parseBoolean(input: string | null, defaultValue: boolean): boolean {
        if (!input) {
            return defaultValue;
        }

        const value = input.trim().toLowerCase();
        if (value === 'true') {
            return true;
        }

        if (value === 'false') {
            return false;
        }

        return defaultValue;
    }

    private sanitizeRegion(input: string | null): string {
        if (!input) {
            return DEFAULT_LOCATION;
        }

        const value = input.trim().toLowerCase();
        if (!ALLOWED_REGION_PATTERN.test(value)) {
            return DEFAULT_LOCATION;
        }

        return value;
    }

    private sanitizeText(input: string | null): string {
        if (!input) {
            return '';
        }

        const trimmed = input.trim();
        if (!trimmed) {
            return '';
        }

        if (!ALLOWED_TEXT_PATTERN.test(trimmed)) {
            return '';
        }

        return trimmed;
    }

    private sanitizeGlQrUrl(input: string | null): string {
        if (!input) {
            return '';
        }

        const trimmed = input.trim();
        if (!trimmed || trimmed.length > ShellUrlConfigService.MAX_QRURL_LENGTH) {
            return '';
        }

        try {
            const url = new URL(trimmed);
            if (url.protocol !== 'https:') {
                return '';
            }

            if (!ALLOWED_GL_QR_URL_ORIGINS.has(url.origin)) {
                return '';
            }
        } catch {
            return '';
        }

        return trimmed;
    }

    private validateId(input: string, pattern: RegExp): string {
        const sanitized = input.replace(/[^0-9a-zA-Z-]/g, '').trim();
        if (!sanitized) {
            return '';
        }

        if (!pattern.test(sanitized)) {
            return '';
        }

        return sanitized;
    }
}
